import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Users, Clock, Smartphone, ArrowRight } from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();

  const trustCues = [
    { icon: Smartphone, text: "No app download for guests" },
    { icon: Users, text: "Works for couples & groups" },
    { icon: Clock, text: "Takes about 60 seconds" },
  ];

  return (
    <main className="app-container">
      <div className="flex-1 flex flex-col px-6 py-8 overflow-hidden">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-4"
        >
          <span className="text-lg font-black tracking-tight text-forkyeah-main">
            Fork<span className="text-forkyeah-primary">Yeah</span>
          </span>
        </motion.div>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-6"
        >
          <h1 
            data-testid="app-title"
            className="text-3xl sm:text-4xl font-black tracking-tight text-forkyeah-main leading-tight mb-3"
          >
            Stop arguing about where to eat.
          </h1>
          <p className="text-forkyeah-muted font-semibold text-base">
            Start a 60-second swipe session and match with your group instantly.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-1 flex items-center justify-center min-h-0 mb-6 relative"
        >
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/3184183/pexels-photo-3184183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Friends deciding where to eat"
              className="max-h-[280px] w-auto object-contain drop-shadow-xl"
              data-testid="hero-image"
            />
            {/* Floating badges */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -left-2 top-1/4 bg-white rounded-full px-3 py-1.5 shadow-lg"
            >
              <span className="text-xs font-bold text-forkyeah-primary">Swipe together</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute -right-2 top-1/2 bg-forkyeah-primary rounded-full px-3 py-1.5 shadow-lg"
            >
              <span className="text-xs font-bold text-white">Match instantly</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Trust Cues */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2 mb-6"
        >
          {trustCues.map((cue, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-forkyeah-accent/30 flex items-center justify-center flex-shrink-0">
                <cue.icon className="w-4 h-4 text-forkyeah-primary" strokeWidth={2.5} />
              </div>
              <span className="text-sm font-semibold text-forkyeah-muted">{cue.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            data-testid="start-session-btn"
            onClick={() => navigate("/setup")}
            className="btn-primary gap-2 group"
          >
            <Zap className="w-5 h-5" strokeWidth={2.5} />
            Start a session
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
          </button>
        </motion.div>

        {/* Bottom tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center text-xs text-forkyeah-muted/70 mt-4 font-medium"
        >
          Faster than a group chat. More fun than polling.
        </motion.p>
      </div>
    </main>
  );
}
